import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar, {
  BusinessHoursInput,
  SlotLabelContentArg,
} from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import {
  ScheduledEvent,
  ScheduledEventColourCoding,
} from '@payaca/types/scheduledEventsTypes';
import { useLatest } from '@payaca/hooks';
import { User } from '@payaca/types/userTypes';
import UntitledIcon from '@payaca/untitled-icons';
import React, { FC, useLayoutEffect, useMemo, useRef, type JSX } from 'react';
import UserIndicator from '../userIndicator/UserIndicator';
import Calendar from './Calendar';
import {
  buildTeamGroupKey,
  buildTeamResourceId,
  NO_TEAM_GROUP_KEY,
  pruneStaleTeamIds,
  resourceIdToUserId,
  teamGroupKeyToGroupId,
  teamGroupKeyToLabel,
  UNASSIGNED_GROUP_KEY,
  UNASSIGNED_RESOURCE_ID,
} from './resourceGrouping';
import SlotLabel from './SlotLabel';
import './UserResourceCalendar.sass';

// Re-exported so consumers can resolve these via this (.tsx) module rather than
// a deep `.ts` import, which the app's bundler does not resolve.
export {
  NO_TEAM_GROUP_ID,
  pruneStaleTeamIds,
  resourceIdToUserId,
  UNASSIGNED_GROUP_ID,
} from './resourceGrouping';

export type TeamRef = { id: string; name: string };

type CalendarUser = Pick<
  User,
  | 'id'
  | 'firstname'
  | 'lastname'
  | 'name'
  | 'email'
  | 'userColour'
  | 'avatarUrl'
>;

type ResourceRow = {
  id: string | number;
  title: number;
  sortIndex: number;
  teamGroup?: string;
};

const getResources = (
  users: CalendarUser[] | undefined,
  showUnassigned: boolean
): ResourceRow[] => {
  const userResources: ResourceRow[] = [];

  if (showUnassigned) {
    userResources.push({
      id: UNASSIGNED_RESOURCE_ID,
      title: 0,
      sortIndex: 0,
    });
  }

  userResources.push(
    ...(users
      ? users.map((u, i) => ({
          id: u.id,
          title: u.id,
          sortIndex: i + 1,
        }))
      : [])
  );

  return userResources;
};

/**
 * Grouped-by-team resources: one row per (user x visible team). Teamless users
 * fall under a "No team" group; the unassigned row keeps its own group. A team
 * filter (`selectedTeamIds`) hides every other team's rows.
 */
const getGroupedResources = (
  users: CalendarUser[] | undefined,
  showUnassigned: boolean,
  userTeams: Map<number, TeamRef[]>,
  allTeams: TeamRef[],
  selectedTeamIds: string[]
): ResourceRow[] => {
  // 1-based so real teams sort after the Unassigned bucket (order index 0).
  const teamOrderIndex = new Map<string, number>();
  allTeams.forEach((team, i) => teamOrderIndex.set(team.id, i + 1));

  const activeTeamIds = pruneStaleTeamIds(selectedTeamIds, allTeams);
  const teamFilterActive = activeTeamIds.length > 0;
  const isTeamVisible = (teamId: string) =>
    teamOrderIndex.has(teamId) &&
    (!teamFilterActive || activeTeamIds.includes(teamId));

  const resources: ResourceRow[] = [];

  // The unassigned (no-user) row always shows when permitted, even under a team
  // filter — unassigned work isn't team-scoped and should stay visible to
  // dispatch onto a team's members.
  if (showUnassigned) {
    resources.push({
      id: UNASSIGNED_RESOURCE_ID,
      title: 0,
      sortIndex: 0,
      teamGroup: UNASSIGNED_GROUP_KEY,
    });
  }

  (users ?? []).forEach((user, i) => {
    const teams = userTeams.get(user.id) ?? [];
    // Only teams present in the account team list can be grouped/ordered. A user
    // whose teams are all absent (teamless, or teams not yet loaded) falls under
    // "No team" rather than vanishing.
    const knownTeams = teams.filter((team) => teamOrderIndex.has(team.id));
    const visibleTeams = knownTeams.filter((team) => isTeamVisible(team.id));

    if (knownTeams.length === 0) {
      // Show under "No team" unless a team filter is narrowing the view.
      if (!teamFilterActive) {
        resources.push({
          id: buildTeamResourceId(user.id, null),
          title: user.id,
          sortIndex: i + 1,
          teamGroup: NO_TEAM_GROUP_KEY,
        });
      }
      return;
    }

    visibleTeams.forEach((team) => {
      resources.push({
        id: buildTeamResourceId(user.id, team.id),
        title: user.id,
        sortIndex: i + 1,
        teamGroup: buildTeamGroupKey(
          teamOrderIndex.get(team.id) ?? 0,
          team.name,
          team.id
        ),
      });
    });
  });

  return resources;
};

const toExpansionId = (groupKey: string) => `teamGroup:${groupKey}`;

type Props = {
  additionalCalendarProps?: any;
  onExistingEventSelect: (event: ScheduledEvent) => void;
  onDateSelect: (dateSelectInfo?: {
    beginAt: string;
    endAt?: string;
    userAssignments?: number[];
  }) => void;
  onViewableDateChange?: (dateInfo: any) => void;
  users?: CalendarUser[];
  scheduledEvents: ScheduledEvent[];
  showUnassigned?: boolean;
  /** When true, rows are grouped under collapsible team headers. */
  groupByTeam?: boolean;
  /** Team membership per user id, used to build the grouped rows. */
  userTeams?: Map<number, TeamRef[]>;
  /** All account teams, name-sorted — drives group order + the No-team bucket. */
  allTeams?: TeamRef[];
  /** When non-empty, only these teams' groups are shown. */
  selectedTeamIds?: string[];
  /** Team ids, `NO_TEAM_GROUP_ID` or `UNASSIGNED_GROUP_ID`. */
  collapsedGroupIds?: string[];
  onGroupCollapsedChange?: (groupId: string, isCollapsed: boolean) => void;
  eventDrop?: any;
  eventResize?: any;
  onViewTypeChange?: (viewType: string) => void;
  initialView?: string;
  minTime?: string;
  maxTime?: string;
  hiddenDays?: number[];
  className?: string;
  scheduledEventColourCoding?: ScheduledEventColourCoding;
  calendarRef?: React.RefObject<FullCalendar | null>;
  isExternallyControlled?: boolean;
  businessHours?: BusinessHoursInput;
};
const UserResourceCalendar: FC<Props> = ({
  additionalCalendarProps = {},
  onExistingEventSelect,
  onDateSelect,
  onViewableDateChange,
  users,
  scheduledEvents,
  showUnassigned = true,
  groupByTeam = false,
  userTeams,
  allTeams,
  selectedTeamIds,
  collapsedGroupIds,
  onGroupCollapsedChange,
  eventDrop,
  eventResize,
  onViewTypeChange,
  initialView,
  minTime,
  maxTime,
  hiddenDays,
  className,
  scheduledEventColourCoding,
  calendarRef: externalCalendarRef,
  isExternallyControlled,
  businessHours,
}: Props): JSX.Element => {
  const ownCalendarRef = useRef<FullCalendar>(null);
  const calendarRef = externalCalendarRef ?? ownCalendarRef;
  const onGroupCollapsedChangeRef = useLatest(onGroupCollapsedChange);
  const resources = useMemo(
    () =>
      groupByTeam
        ? getGroupedResources(
            users,
            showUnassigned,
            userTeams ?? new Map(),
            allTeams ?? [],
            selectedTeamIds ?? []
          )
        : getResources(users, showUnassigned),
    [groupByTeam, users, showUnassigned, userTeams, allTeams, selectedTeamIds]
  );

  // FullCalendar keeps group expansion in its own state, keyed by group field
  // and value, so push the persisted state in whenever the groups change.
  useLayoutEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api || !groupByTeam || !collapsedGroupIds) return;
    const expansions = api.getCurrentData().resourceEntityExpansions ?? {};
    new Set(resources.map((resource) => resource.teamGroup)).forEach(
      (groupKey) => {
        if (!groupKey) return;
        const id = toExpansionId(groupKey);
        const isExpanded = !collapsedGroupIds.includes(
          teamGroupKeyToGroupId(groupKey)
        );
        if ((expansions[id] ?? true) !== isExpanded) {
          api.dispatch({
            type: 'SET_RESOURCE_ENTITY_EXPANDED',
            id,
            isExpanded,
          });
        }
      }
    );
  }, [calendarRef, groupByTeam, collapsedGroupIds, resources]);

  const parsedEventsWithResources = useMemo(() => {
    const eventsWithResources: any[] = [];

    if (groupByTeam) {
      // Grouped mode: a user has one row per visible team, so an assigned event
      // must appear under every one of that user's rows. Emit a copy per row id
      // that actually exists (unmatched ids simply don't render).
      const userIdToRowIds = new Map<number, (string | number)[]>();
      let hasUnassignedRow = false;
      resources.forEach((resource) => {
        if (resource.id === UNASSIGNED_RESOURCE_ID) {
          hasUnassignedRow = true;
          return;
        }
        const userId = resourceIdToUserId(resource.id);
        if (userId === undefined) return;
        const rowIds = userIdToRowIds.get(userId) ?? [];
        rowIds.push(resource.id);
        userIdToRowIds.set(userId, rowIds);
      });

      scheduledEvents.forEach((se) => {
        const userAssignments = se.userAssignments;
        if (userAssignments?.length) {
          userAssignments.forEach((ua) => {
            (userIdToRowIds.get(ua) ?? []).forEach((rowId) => {
              eventsWithResources.push({ ...se, userAssignment: rowId });
            });
          });
        } else if (hasUnassignedRow) {
          eventsWithResources.push({
            ...se,
            userAssignment: UNASSIGNED_RESOURCE_ID,
          });
        }
      });

      return eventsWithResources;
    }

    scheduledEvents.forEach((se) => {
      // add event for each user
      const userAssignments = se.userAssignments;
      if (userAssignments?.length) {
        userAssignments.forEach((ua) => {
          eventsWithResources.push({
            ...se,
            userAssignment: ua,
          });
        });
      } else {
        // add event with unassigned user
        eventsWithResources.push({
          ...se,
          userAssignment: 'unassigned',
        });
      }
    });
    return eventsWithResources;
  }, [scheduledEvents, groupByTeam, resources]);

  return (
    <Calendar
      isExternallyControlled={isExternallyControlled}
      additionalCalendarProps={{
        views: {
          day: {
            titleFormat: 'ddd, D MMM YYYY',
          },
        },
        resourceOrder: groupByTeam ? 'teamGroup,sortIndex' : 'sortIndex',
        resources,
        ...(groupByTeam
          ? {
              resourceGroupField: 'teamGroup',
              resourcesInitiallyExpanded: true,
              resourceGroupLabelContent: (arg: { groupValue: string }) =>
                teamGroupKeyToLabel(arg.groupValue),
              // FullCalendar has no expand/collapse callback, so take the
              // expander click over: its own toggle would run after React has
              // already re-applied the new state, flipping it straight back.
              resourceGroupLabelDidMount: (arg: {
                el: HTMLElement;
                groupValue: string;
              }) => {
                arg.el.addEventListener(
                  'click',
                  (event) => {
                    const api = calendarRef.current?.getApi();
                    if (
                      !api ||
                      !(event.target instanceof Element) ||
                      !event.target.closest('.fc-datagrid-expander')
                    ) {
                      return;
                    }
                    event.stopPropagation();
                    const id = toExpansionId(arg.groupValue);
                    const isExpanded = !(
                      api.getCurrentData().resourceEntityExpansions?.[id] ??
                      true
                    );
                    api.dispatch({
                      type: 'SET_RESOURCE_ENTITY_EXPANDED',
                      id,
                      isExpanded,
                    });
                    onGroupCollapsedChangeRef.current?.(
                      teamGroupKeyToGroupId(arg.groupValue),
                      !isExpanded
                    );
                  },
                  { capture: true }
                );
              },
            }
          : {}),
        resourceAreaWidth: '15%',
        resourceAreaHeaderContent: ' ',
        resourceLabelContent: (resource: {
          fieldValue: string;
          resource: any;
          view: any;
        }) => {
          const user = users?.find((u) => u.id === +resource.fieldValue);
          if (user) {
            return (
              <div className="user-resource">
                <UserIndicator
                  user={{
                    firstName: user.firstname,
                    lastName: user.lastname,
                    emailAddress: user.email,
                    userColour: user.userColour,
                    imgSrc: user.avatarUrl,
                  }}
                  size="sm"
                />
                <span> {user.name || user.firstname}</span>
              </div>
            );
          }
          return (
            <div className="user-resource">
              <div className="unassigned-user">
                <UntitledIcon name="user-minus-01.3" className="w-4 h-4" />
              </div>
              <span>Unassigned</span>
            </div>
          );
        },
        ...additionalCalendarProps,
      }}
      className={`user-resource-calendar timeline-calendar${
        className ? ` ${className}` : ''
      }`}
      initialView={initialView}
      hasBoxShadow={false}
      headerToolbar={{
        left: 'today,jumpToDate',
        center: 'prev title next',
        right: 'resourceTimelineMonth,resourceTimelineWeek,resourceTimelineDay',
      }}
      onDateSelect={onDateSelect}
      onExistingEventSelect={onExistingEventSelect}
      onViewableDateChange={onViewableDateChange}
      plugins={[interactionPlugin, resourceTimelinePlugin]}
      scheduledEvents={parsedEventsWithResources}
      renderSlotLabelContent={(slotLabelContentArg: SlotLabelContentArg) => {
        return <SlotLabel slotLabelContentArg={slotLabelContentArg} />;
      }}
      eventDrop={eventDrop}
      eventResize={eventResize}
      onViewTypeChange={onViewTypeChange}
      minTime={minTime}
      maxTime={maxTime}
      hiddenDays={hiddenDays}
      scheduledEventColourCoding={scheduledEventColourCoding}
      externalCalendarRef={calendarRef}
      businessHours={businessHours}
    />
  );
};

export default UserResourceCalendar;
